import { signedFetch } from '../../super-picker/services/api';

const RAW_API_URL = process.env.REACT_APP_API_URL || '/';
const API_ROOT = RAW_API_URL.replace(/\/+$/, '');
const USE_HOSTING_PROXY = API_ROOT === '' || API_ROOT === '/';
const FUNCTION_BASE = USE_HOSTING_PROXY ? '' : (API_ROOT.endsWith('/api') ? API_ROOT : `${API_ROOT}/api`);
const API_BASE = USE_HOSTING_PROXY ? '/api/project-essential' : `${FUNCTION_BASE}/api/project-essential`;

export interface UpcomingProject {
  id: string;
  name: string | null;
  developer: string | null;
  coverImage: string | null;
  region: string | null;
  area: string | null;
  district: string | null;
  address: string | null;
  projectType: string | null;
  minimumPrice: number | null;
  maximumPrice: number | null;
  upcomingSeq: number | null;
  /**
   * ISO timestamp of the project's launch date. The dashboard carousel
   * shows upcoming projects (future launch) before GLS rows.
   * `null` for projects that haven't been dated yet.
   */
  launchDate: string | null;
  /**
   * Curated free-text unit count (e.g. "45", "200 Units", "120-180").
   * Falls back to the numeric `TotalUnitNumber` server-side when the
   * curated label is blank, so the FE can always render a "<n> Units"
   * line when any unit info exists. `null` when both are missing.
   */
  totalUnits: string | null;
  /**
   * Expected Top-of-Project year (`Project.TOPDate`, an integer year
   * such as 2026). Used to render the "Est. <year>" line on the
   * carousel cards. `null` when the project has no expected TOP set.
   */
  topDate: number | null;
  /** False when the Project Resources dialog would be empty for this project. */
  hasMaterials?: boolean;
}

/**
 * A ProjectLandSale row. The listing (`GET /gls-land-sales`) is unlinked
 * pipeline sites (ProjectName IS NULL); the detail (`GET /gls-land-sales/:id`)
 * returns any row by Id, including awarded land reports linked via NewProjectId.
 * The `id` is the numeric `ProjectLandSale.Id`; `name` comes from `SiteName`;
 * `coverImage` from `MapImage`.
 */
export interface GlsLandSaleItem {
  id: number;
  /**
   * `ProjectLandSale.ProjectId` — the Project.Id used to join
   * `EDMProject` for materials. Null when the land sale is not yet
   * linked to a Project row.
   */
  projectId: string | null;
  /**
   * `ProjectLandSale.NewProjectId` — awarded/linked project once the
   * site becomes a real Project. Materials still resolve via `projectId`.
   */
  newProjectId?: string | null;
  name: string | null;
  coverImage: string | null;
  location: string | null;
  region: string | null;
  district: string | null;
  planningArea: string | null;
  nearbyMrtStation: string | null;
  landUse: string | null;
  landSaleType: string | null;
  tenure: string | null;
  leasePeriod: string | null;
  siteAreaSqm: number | null;
  siteAreaSqft: number | null;
  grossPlotRatioSqft: number | null;
  housingUnits: number | null;
  successfulTenderer: string | null;
  successfulTenderPrice: number | null;
  uraExpectedTop: string | null;
  launchDate: string | null;
  closingDate: string | null;
  awardDate: string | null;
  downloadLink: string | null;
  downloadCount: number | null;
  siteStatus: string | null;
  dateOfLaunchEst: string | null;
  /** False when the linked project has no resources yet. */
  hasMaterials?: boolean;
}

/** Prefer `ProjectId` for GLS materials; `NewProjectId` is the awarded project. */
export function glsLinkedProjectId(
  item: Pick<GlsLandSaleItem, 'projectId' | 'newProjectId'>,
): string {
  return item.projectId?.trim() || item.newProjectId?.trim() || '';
}

/**
 * Government Land Sale fields joined from `ProjectLandSale` onto a
 * `Project` via `ProjectLandSale.SiteName = Project.Name`. Returned
 * inline on `GET /projects/:id` as `landInfo: LandInfo | null` — the
 * FE uses non-null presence to decide whether to render the "Land
 * info" tab on the project detail page.
 */
export interface LandInfo {
  siteName: string | null;
  /** Surfaces the tender winner under a project-style "Developer" row. */
  developer: string | null;
  location: string | null;
  landUse: string | null;
  landSaleType: string | null;
  region: string | null;
  district: string | null;
  planningArea: string | null;
  nearbyMrtStation: string | null;
  tenure: string | null;
  leasePeriod: string | null;
  siteAreaSqm: number | null;
  siteAreaSqft: number | null;
  /** PSF on Plot Ratio (`GrossPlotRatio_Sqft`) — pre-computed in DB. */
  grossPlotRatioSqft: number | null;
  housingUnits: number | null;
  successfulTenderer: string | null;
  successfulTenderPrice: number | null;
  uraExpectedTop: string | null;
  launchDate: string | null;
  closingDate: string | null;
  awardDate: string | null;
  plsId?: number | null;
  downloadLink?: string | null;
  downloadCount?: number | null;
}

export interface ProjectDetail {
  id: string;
  name: string | null;
  categoryId: string | null;
  country?: string | null;
  developer: string | null;
  coverImage: string | null;
  region: string | null;
  area: string | null;
  district: string | null;
  address: string | null;
  projectType: string | null;
  minimumPrice: number | null;
  maximumPrice: number | null;
  totalUnits: string | null;
  totalUnitNumber: number | null;
  tenure: string | null;
  completeDate: string | null;
  topDate: number | null;
  description: string | null;
  /**
   * `Project.MeetingLink` — Hyper group chat invite, `null` when unset
   * or not a safe http(s) URL.
   */
  meetingLink?: string | null;
  /**
   * Joined `ProjectLandSale` row, when one exists for this project
   * (matched by `SiteName = Project.Name`). `null` for ordinary
   * projects — the FE hides the "Land info" tab in that case.
   */
  landInfo: LandInfo | null;
  /** False when the Project Resources dialog would be empty. */
  hasMaterials?: boolean;
}

export interface ProjectFileItem {
  id: number;
  filename: string;
  displayName: string;
  category: string;
  url: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ProjectFileGroup {
  category: string;
  items: ProjectFileItem[];
}

/**
 * Launcher eBook tied to a specific Project (or set of projects).
 * Returned by `GET /launcher-ebooks?projectId|variant`. The shape is
 * a strict superset of `LatestHighlightItem` — same fields plus the
 * `projectId` of the linked project (`null` if the join row was
 * missing one, which shouldn't happen given the FK).
 */
export interface LauncherEbookItem {
  id: string;
  title: string;
  type: string | null;
  createdAt: string | null;
  /**
   * Linked Project.Id from the ProjectLauncherProject / EDMProject join
   * row. Always `null` for the `periodic` variant — periodic issues are
   * not project-tagged.
   */
  projectId: string | null;
  /** Portal document URL, or the EDM download URL for EDM-backed rows. */
  url: string;
  /** Curated thumbnail — `ProjectLauncher.ThumbnailPath` or `EDM.Thumbnail`. */
  cover: string | null;
  /** Present on EDM-backed rows (IsEbook eBooks and Project Spotlight Ebooks). */
  urlPublic?: string | null;
  filename?: string;
  /** Mock downloads counter (deterministic hash of the row id). */
  downloads: number;
}

/** Latest Highlights item — a periodic or client-engagement eBook from EDM. */
export interface LatestHighlightItem {
  id: string;
  title: string;
  /** Display type, e.g. "Project Spotlight Ebook" or the EDM category. */
  type: string | null;
  /** ISO timestamp from EDM.CreatedAt (nullable). */
  createdAt: string | null;
  /** With-agent EDM download URL. */
  url: string;
  /** Curated thumbnail from `EDM.Thumbnail`. */
  cover?: string | null;
  downloads: number;
}

export interface InsightFileItem {
  id: string;
  filename: string;
  displayName: string;
  subtitle?: string;
  uploadedAt: string | null;
  url: string | null;
  downloadLink?: string | null;
  downloadCount?: number | null;
}

/**
 * Category id understood by `GET /videos`. The backend resolves
 * `resi` (album 9892122), `international` (9892143), `marketing`
 * (9892145) and `consumer` (9892142). `upcoming` is not yet wired
 * and returns an empty payload.
 */
export type VideoCategoryId =
  | 'resi'
  | 'upcoming'
  | 'commercial'
  | 'international'
  | 'marketing'
  | 'consumer';

/** A single `ProjectMedia` row for the project resources Media tab. */
export interface ProjectMediaItem {
  mediaNumber: number;
  type: string | null;
  url: string | null;
  externalLink: string | null;
  description: string | null;
  size: number | null;
  imageSmall: string | null;
  imageMedium: string | null;
  imageLarge: string | null;
  videoThumbnail: string | null;
  updatedAt: string | null;
}

/** A single video row returned by `GET /videos`. */
export interface ProjectVideoItem {
  id: string;
  title: string;
  viewSetting?: string | null;
  /** Cumulative views on the source platform. */
  views: number;
  /** Duration in seconds — frontend formats to mm:ss / h:mm:ss. */
  durationSeconds: number;
  /** ISO timestamp of the upload date. */
  createdAt: string | null;
  /** Absolute portal URL for the cover thumbnail (nullable). */
  thumbnail: string | null;
  /** Absolute portal URL for the streaming source (nullable). */
  streamUrl: string | null;
  /**
   * Absolute portal URL for the supplementary "notes" attachment
   * (sourced from `VideoAttachment.Path`). `null` when the video
   * has no attachment, which is the frontend's signal to hide the
   * "Download Notes" footer button entirely.
   */
  notesUrl: string | null;
  /**
   * Suggested save-as filename (`VideoAttachment.Name`). May be
   * `null` even when `notesUrl` is set — the frontend then derives
   * a filename from the URL or the video title.
   */
  notesFilename: string | null;
  projects?: Array<{
    id: string;
    name: string;
    region: string;
    area: string;
    district: string;
    categoryId: string;
  }>;
}

export interface QuarterlyReportItem {
  id: string;
  title: string;
  /** Absolute portal URL for the cover thumbnail (nullable). */
  cover: string | null;
  /**
   * Download URL with agent details (the composited PDF). `'#'`
   * when the row can't currently be served (e.g. CEA missing).
   */
  url: string;
  /**
   * Download URL without agent details (the raw uploaded asset).
   * `null` when the row has no such variant. Powers the "With Agent
   * Details" toggle in the shared FilePreviewDialog.
   */
  urlPublic: string | null;
  /** Suggested save-as filename. */
  filename: string;
  /** Mock downloads counter (deterministic hash of the row id). */
  downloads: number;
  /** ISO timestamp from `EDM.CreatedAt` (nullable). */
  createdAt: string | null;
}

/** Listing page variant for scoped EDM catalogue queries (`listingVariant` param). */
export type ListingVariant = 'upcoming' | 'all-projects' | 'commercial' | 'international';

/** Tab id understood by `GET /edms/tab/:tab`. */
export type EdmTabId =
  | 'recent'
  | 'all'
  | 'upcoming'
  | 'upcoming-edm-pass'
  | 'commercial-edm-pass'
  | 'international-edm-pass'
  | 'all-projects-edm-pass'
  | 'projects'
  | 'engagement'
  | 'property-guides'
  | 'seminars'
  | 'vvip'
  | 'hidden'
  | 'tagged-ec'
  | 'ebooks';

/** Poster shape returned by `/edms/tab/:tab`. Adapted to the shared `PosterItem` on the page. */
export interface EdmTabItem {
  id: string;
  title: string;
  category: string | null;
  projectId: string | null;
  /** Absolute portal URL for the cover thumbnail (nullable). */
  cover: string | null;
  /**
   * Download URL with agent details (the composited PDF). `'#'`
   * when the row can't currently be served (e.g. CEA missing).
   */
  url: string;
  /**
   * Download URL without agent details (portal "Get Template" / raw
   * image). PDF templates use `/public/DownloadTemplate/` with
   * `userId`; image EDMs use `/Home/Download`. `null` when unavailable
   * (e.g. CEA missing for PDF templates). Download-only.
   */
  urlPublic: string | null;
  /** Suggested save-as filename (`.pdf` or `.jpg` from OutputType). */
  filename: string;
  /** ISO timestamp from EDM.CreatedAt (nullable). */
  createdAt: string | null;
  /** Mock downloads counter (deterministic hash of the row id). */
  downloads: number;
  isEbook?: boolean | null;
}

export interface EdmItem {
  id: string;
  category: string | null;
  name: string;
  projectId: string | null;
  thumbnail: string | null;
  orientation: string | null;
  outputType: string | null;
  placeHolderPosition: string | null;
  whatsAppQrCodeMsg: string | null;
  expiryDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  displayOrder: number | null;
  isEbook?: boolean | null;
}

/** Seminar/event row returned by `GET /events`. */
export interface SeminarEventItem {
  id: string;
  name: string;
  /** ISO timestamp for the event start (nullable for resilience). */
  start: string | null;
  /** ISO timestamp for the event end (nullable). */
  end: string | null;
  venue: string | null;
  description: string | null;
  /** Backend CategoryId (e.g. 'C17046'). */
  category: string | null;
  registrationRequired: boolean | null;
}

export interface ProjectFilters {
  regions?: string[];
  districts?: string[];
  /** Property-type bucket ids: 'condo' | 'ec' | 'mixed' | 'landed' | 'commercial' | 'international' */
  propertyTypes?: string[];
  /**
   * Explicit project ids. When set, region / district / property-type
   * filters are ignored — the two modes are mutually exclusive in the
   * filter dialog.
   */
  projectIds?: string[];
  /** When true, restrict to projects with SoleMarketing set. */
  soleMarketing?: boolean;
}

/** One hit from `GET /search`. */
export interface UniversalSearchItem {
  id: string;
  title: string;
  subtitle: string | null;
  thumbnail: string | null;
  href: string;
  kind: 'project' | 'gls' | 'edm' | 'ebook' | 'highlight' | 'file';
  createdAt: string | null;
  launchDate: string | null;
  /** With-agent / primary preview URL when the hit is previewable. */
  url?: string | null;
  urlPublic?: string | null;
  filename?: string | null;
  /** Download counter for poster cards (EDM.Counter / research files). */
  downloads?: number | null;
}

/** One labelled bucket from `GET /search`. */
export interface UniversalSearchGroup {
  id: string;
  label: string;
  total: number;
  /** Destination path for "View more" (may already include tab/section). */
  viewMoreHref: string | null;
  items: UniversalSearchItem[];
}

export interface UniversalSearchResponse {
  groups: UniversalSearchGroup[];
}

export interface FilterDistrict {
  no: string;
  name: string;
  region: string | null;
}

export interface FilterPropertyType {
  id: string;
  label: string;
}

export interface FilterOptions {
  regions: string[];
  districts: FilterDistrict[];
  propertyTypes: FilterPropertyType[];
}

export interface ProjectEssentialUser {
  id?: string;
  name?: string;
  Name?: string;
  email?: string;
  role?: string;
  photo?: string;
  Photo?: string;
}

/**
 * One `/user/me` per page load. Every card's resources dialog asks for the
 * role on mount, so an uncached call fans out into dozens of authenticated
 * requests that queue behind each other. A failed call is not kept, so the
 * next caller retries.
 */
let currentUserPromise: Promise<ProjectEssentialUser> | null = null;

/** Seed the cache with the user the access check already loaded. */
export function primeProjectEssentialUser(user: ProjectEssentialUser): void {
  currentUserPromise = Promise.resolve(user);
}

function getCurrentUser(): Promise<ProjectEssentialUser> {
  if (!currentUserPromise) {
    currentUserPromise = getJson<ProjectEssentialUser>(`${API_BASE}/user/me`).catch((error) => {
      currentUserPromise = null;
      throw error;
    });
  }
  return currentUserPromise;
}

/**
 * Build the URL for the in-product preview proxy. The browser
 * can't render the portal's Download endpoints inline (they're
 * served with `Content-Disposition: attachment` and behind a
 * cross-origin host with no CORS), so the FilePreviewDialog goes
 * through this server-side proxy which rewrites the disposition to
 * `inline` and terminates the cross-origin hop on our backend.
 *
 * Returns `null` for empty/placeholder inputs so the dialog can fall
 * back to "preview unavailable" without ever firing a request.
 */
export function buildPreviewProxyUrl(targetUrl: string | null | undefined): string | null {
  if (!targetUrl || targetUrl === '#') return null;
  const trimmed = targetUrl.trim();
  if (!trimmed) return null;
  return `${API_BASE}/preview/proxy?url=${encodeURIComponent(trimmed)}`;
}

/** In-memory expiry for the HMAC scrape-grant cookie (ms epoch). */
let scrapeGrantExpiresAt = 0;
let scrapeGrantValue: string | null = null;
let scrapeGrantInflight: Promise<void> | null = null;

/**
 * Ensure a short-lived scrape-grant cookie exists before calling
 * `publicWebScrape` preview routes. Those routes cannot verify access
 * JWTs when ACCESS_TOKEN_PUBLIC_KEY is missing on that Cloud Run
 * service; the main `api` mints an HMAC grant instead.
 */
export async function ensureScrapeGrant(): Promise<string> {
  if (scrapeGrantValue && Date.now() < scrapeGrantExpiresAt - 30_000) {
    return scrapeGrantValue;
  }
  if (scrapeGrantInflight) {
    await scrapeGrantInflight;
    if (!scrapeGrantValue) throw new Error('Could not mint preview grant');
    return scrapeGrantValue;
  }

  scrapeGrantInflight = (async () => {
    const data = await getJson<{ ok?: boolean; expiresAt?: number; grant?: string }>(
      `${API_BASE}/scrape-grant`,
    );
    if (!data.grant) {
      throw new Error('Could not mint preview grant');
    }
    scrapeGrantValue = data.grant;
    scrapeGrantExpiresAt =
      typeof data.expiresAt === 'number' && Number.isFinite(data.expiresAt)
        ? data.expiresAt
        : Date.now() + 9 * 60 * 1000;
  })().finally(() => {
    scrapeGrantInflight = null;
  });

  await scrapeGrantInflight;
  if (!scrapeGrantValue) throw new Error('Could not mint preview grant');
  return scrapeGrantValue;
}

function scrapeGrantHeaders(grant: string): HeadersInit {
  return { 'X-Preview-Grant': grant };
}

/**
 * Fetch a previewable file through the backend proxy and return a
 * browser-side object URL plus the upstream MIME type. The dialog
 * is responsible for `URL.revokeObjectURL` when the URL changes or
 * the dialog closes.
 */
/** Portal `/public/DownloadEDM/<id>` URL used for with-agent downloads. */
export function parseEdmIdFromDownloadEdmUrl(
  rawUrl: string | null | undefined,
): string | null {
  if (!rawUrl || rawUrl === '#') return null;
  try {
    const parsed = new URL(rawUrl);
    const match = parsed.pathname.match(/\/public\/DownloadEDM\/([^/]+)/i);
    if (!match?.[1]) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function isDownloadEdmUrl(rawUrl: string | null | undefined): boolean {
  return parseEdmIdFromDownloadEdmUrl(rawUrl) !== null;
}

/**
 * Resolve the portal temp asset URL for a with-agent EDM preview via
 * `/public/GetEDM/<id>` (PDF or image). The returned URL can be
 * rendered directly without the download proxy.
 */
export async function resolveEdmWithAgentPreviewUrl(edmId: string): Promise<string> {
  const grant = await ensureScrapeGrant();
  const response = await signedFetch(
    `${API_BASE}/preview/edm-with-agent?edmId=${encodeURIComponent(edmId)}`,
    { headers: scrapeGrantHeaders(grant) },
    true,
    false,
  );
  const data = (await response.json().catch(() => ({}))) as {
    previewUrl?: string;
    error?: string;
  };
  if (!response.ok || !data.previewUrl) {
    throw new Error(data.error || 'Could not resolve EDM preview URL');
  }
  return data.previewUrl;
}

export async function fetchPreviewBlob(
  targetUrl: string,
): Promise<{ objectUrl: string; contentType: string }> {
  const grant = await ensureScrapeGrant();
  const proxyUrl = buildPreviewProxyUrl(targetUrl);
  if (!proxyUrl) {
    throw new Error('No preview URL');
  }
  const response = await signedFetch(
    proxyUrl,
    { headers: scrapeGrantHeaders(grant) },
    true,
    false,
  );
  if (!response.ok) {
    throw new Error(`Preview fetch failed: ${response.status}`);
  }
  const contentType = response.headers.get('content-type') || 'application/octet-stream';
  const blob = await response.blob();
  return { objectUrl: URL.createObjectURL(blob), contentType };
}

/**
 * Object URL for a server-rendered JPEG of a portal PDF's first page.
 *
 * Rasterising a PDF in the browser means downloading the whole file and
 * parsing it in pdf.js, which is how a list of brochures gets the iOS WebView
 * killed. This endpoint does the raster on the server and caches it, so a list
 * row costs ~30 KB instead of ~30 MB.
 *
 * `width` is the rendered pixel width — ask for roughly 2× the CSS box so the
 * thumbnail still looks sharp on a retina screen.
 *
 * Throws when no thumbnail is available (unrenderable, oversized, or the
 * renderer is saturated); callers should fall back to a file-type icon.
 */
export async function fetchPdfThumbnailBlob(
  targetUrl: string,
  width: number,
): Promise<string> {
  const trimmed = (targetUrl || '').trim();
  if (!trimmed || trimmed === '#') throw new Error('No preview URL');
  const grant = await ensureScrapeGrant();
  const response = await signedFetch(
    `${API_BASE}/preview/pdf-thumbnail?url=${encodeURIComponent(trimmed)}&w=${width}`,
    { headers: scrapeGrantHeaders(grant) },
    true,
    false,
  );
  if (!response.ok) {
    throw new Error(`Thumbnail fetch failed: ${response.status}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

async function getJson<T>(url: string): Promise<T> {
  const response = await signedFetch(url);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((payload as { error?: string })?.error || `Request failed: ${response.status}`);
  }
  return payload as T;
}

/**
 * A `MonthRange`-compatible pair `{ start: 'YYYY-MM', end: 'YYYY-MM' }`.
 * Declared locally to avoid pulling a component import into the API layer
 * — the shape is intentionally minimal so any caller can construct it.
 */
export interface MonthRangeParams {
  start: string;
  end: string;
}

/**
 * Append `from`/`to` query params from a `MonthRangeParams`. Skips
 * anything that doesn't look like `YYYY-MM` so the backend never sees
 * a malformed value and the request stays cache-friendly.
 */
function appendMonthRangeParams(
  params: URLSearchParams,
  range?: MonthRangeParams | null,
): void {
  if (!range) return;
  const looksValid = (v: string) => /^\d{4}-\d{2}$/.test(v);
  if (range.start && looksValid(range.start)) params.set('from', range.start);
  if (range.end && looksValid(range.end)) params.set('to', range.end);
}

function appendProjectFilterParams(params: URLSearchParams, filters?: ProjectFilters): void {
  if (filters?.projectIds?.length) {
    params.set('projectId', filters.projectIds.join(','));
  } else {
    if (filters?.regions?.length) params.set('region', filters.regions.join(','));
    if (filters?.districts?.length) params.set('district', filters.districts.join(','));
    if (filters?.propertyTypes?.length) {
      params.set('propertyType', filters.propertyTypes.join(','));
    }
  }
  if (filters?.soleMarketing) params.set('soleMarketing', '1');
}

export interface PeRecentSearchItem {
  id: number;
  itemType: string;
  itemId: string;
  title: string;
  thumbnail: string | null;
  href: string | null;
  updatedAt: string | null;
}

export const projectEssentialApi = {
  getMe: getCurrentUser,
  searchContent: (query?: string, filters?: ProjectFilters) => {
    const params = new URLSearchParams();
    const trimmed = (query || '').trim();
    if (trimmed) params.set('q', trimmed);
    appendProjectFilterParams(params, filters);
    const qs = params.toString();
    const url = qs ? `${API_BASE}/search?${qs}` : `${API_BASE}/search`;
    return getJson<UniversalSearchResponse>(url);
  },
  getRecentSearches: () =>
    getJson<{ items: PeRecentSearchItem[] }>(`${API_BASE}/search/recent`),
  recordRecentSearch: (payload: {
    itemType: string;
    itemId: string;
    title: string;
    thumbnail?: string | null;
    href?: string | null;
  }) =>
    signedFetch(`${API_BASE}/search/recent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(async (res) => {
      const payloadJson = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          (payloadJson as { error?: string })?.error || 'Failed to save recent search',
        );
      }
      return payloadJson as { item: PeRecentSearchItem };
    }),
  getUpcomingProjects: (query?: string, filters?: ProjectFilters, variant?: string) => {
    const params = new URLSearchParams();
    const trimmed = (query || '').trim();
    if (trimmed) params.set('q', trimmed);
    if (variant) params.set('variant', variant);
    appendProjectFilterParams(params, filters);
    const qs = params.toString();
    const url = qs ? `${API_BASE}/upcoming-projects?${qs}` : `${API_BASE}/upcoming-projects`;
    return getJson<{ items: UpcomingProject[]; total: number }>(url);
  },
  /**
   * Resi (all-projects) + commercial + international listing pools
   * merged for the Project filter dropdown.
   */
  getFilterProjectPool: async () => {
    const [resi, commercial, international] = await Promise.all([
      getJson<{ items: UpcomingProject[]; total: number }>(
        `${API_BASE}/upcoming-projects?variant=all-projects`,
      ),
      getJson<{ items: UpcomingProject[]; total: number }>(
        `${API_BASE}/upcoming-projects?variant=commercial`,
      ),
      getJson<{ items: UpcomingProject[]; total: number }>(
        `${API_BASE}/upcoming-projects?variant=international`,
      ),
    ]);
    const byId = new Map<string, UpcomingProject>();
    for (const p of [...resi.items, ...commercial.items, ...international.items]) {
      byId.set(p.id, p);
    }
    return Array.from(byId.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
    );
  },
  getFeaturedProjects: () => getJson<{ items: UpcomingProject[]; total: number }>(`${API_BASE}/featured-projects`),
  getFilterOptions: () => getJson<FilterOptions>(`${API_BASE}/filter-options`),
  getProjectDetail: (id: string) =>
    getJson<ProjectDetail>(`${API_BASE}/projects/${encodeURIComponent(id)}`),
  getProjectFiles: (id: string) =>
    getJson<{ groups: ProjectFileGroup[]; total: number }>(
      `${API_BASE}/projects/${encodeURIComponent(id)}/files`,
    ),
  getEdms: (category?: string, limit = 30, isEbook?: boolean) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    params.set('limit', String(limit));
    if (isEbook !== undefined) params.set('isEbook', String(isEbook));
    return getJson<{ items: EdmItem[]; total: number }>(`${API_BASE}/edms?${params.toString()}`);
  },
  /**
   * Every valid, non-expired EDM tagged via `EDMProject`. Powers the
   * resources dialog's "EDM & VVIP Passes" / eBook tabs.
   *
   * Pass either a normal `projectId`, or `landSaleId` for Upcoming GLS
   * (ProjectLandSale → EDMProject on ProjectId where ProjectName IS NULL).
   */
  getEdmsByProject: (
    opts: string | { projectId: string } | { landSaleId: string | number },
    limit?: number,
    isEbook?: boolean,
  ) => {
    const params = new URLSearchParams();
    if (typeof opts === 'string') {
      params.set('projectId', opts);
    } else if ('landSaleId' in opts) {
      params.set('landSaleId', String(opts.landSaleId));
    } else {
      params.set('projectId', opts.projectId);
    }
    if (limit != null) params.set('limit', String(limit));
    if (isEbook != null) params.set('isEbook', String(isEbook));
    return getJson<{ items: EdmTabItem[]; total: number }>(
      `${API_BASE}/edms/by-project?${params.toString()}`,
    );
  },
  getEdmsByTab: (
    tab: EdmTabId,
    limit?: number,
    q?: string,
    filters?: ProjectFilters,
    monthRange?: MonthRangeParams | null,
    listingVariant?: ListingVariant,
  ) => {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    const trimmed = (q || '').trim();
    if (trimmed) params.set('q', trimmed);
    if (listingVariant) params.set('listingVariant', listingVariant);
    appendProjectFilterParams(params, filters);
    appendMonthRangeParams(params, monthRange);
    const qs = params.toString();
    const url = qs
      ? `${API_BASE}/edms/tab/${encodeURIComponent(tab)}?${qs}`
      : `${API_BASE}/edms/tab/${encodeURIComponent(tab)}`;
    return getJson<{ items: EdmTabItem[]; total: number }>(url);
  },
  /** Row counts per catalogue chip on the Project EDM page. */
  getEdmTabCounts: (
    q?: string,
    filters?: ProjectFilters,
    monthRange?: MonthRangeParams | null,
    listingVariant?: ListingVariant,
  ) => {
    const params = new URLSearchParams();
    const trimmed = (q || '').trim();
    if (trimmed) params.set('q', trimmed);
    if (listingVariant) params.set('listingVariant', listingVariant);
    appendProjectFilterParams(params, filters);
    appendMonthRangeParams(params, monthRange);
    const qs = params.toString();
    const url = qs ? `${API_BASE}/edms/tab-counts?${qs}` : `${API_BASE}/edms/tab-counts`;
    return getJson<{ counts: Partial<Record<EdmTabId, number>> }>(url);
  },
  getQuarterlyReports: (limit = 30) =>
    getJson<{ items: QuarterlyReportItem[]; total: number }>(
      `${API_BASE}/quarterly-reports?limit=${limit}`,
    ),
  getEvents: (opts?: { from?: string; to?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.from) params.set('from', opts.from);
    if (opts?.to) params.set('to', opts.to);
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    const qs = params.toString();
    const url = qs ? `${API_BASE}/events?${qs}` : `${API_BASE}/events`;
    return getJson<{ items: SeminarEventItem[]; total: number }>(url);
  },
  getInsights: (limit = 50) =>
    getJson<{ items: InsightFileItem[]; total: number }>(
      `${API_BASE}/insights?limit=${limit}`,
    ),
  getLatestHighlights: (limit = 10, monthRange?: MonthRangeParams | null, filters?: ProjectFilters) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    appendMonthRangeParams(params, monthRange);
    appendProjectFilterParams(params, filters);
    return getJson<{ items: LatestHighlightItem[]; total: number }>(
      `${API_BASE}/highlights/latest?${params.toString()}`,
    );
  },
  /**
   * Launcher eBooks scoped either to a single project (`projectId`)
   * or to a project bucket (`variant=upcoming|commercial`). Backed by
   * the new `ProjectLauncherProject` join table. Exactly one of the
   * two scoping options must be supplied; the request is a 400
   * otherwise.
   *
   * `variant=periodic` is the exception: those rows come from `EDM`
   * (`Category = 'Periodicebooks'`, `Status = 'Valid'`), not
   * `ProjectLauncher`, and carry EDM download URLs.
   */
  getLauncherEbooks: (
    opts:
      | { projectId: string }
      | {
          variant:
            | 'upcoming'
            | 'upcoming-projects'
            | 'commercial'
            | 'international'
            | 'all-projects'
            | 'periodic';
          /** Restricts Project Spotlight Ebooks to a listing-page project bucket. */
          listingVariant?: ListingVariant;
        },
    limit?: number,
    monthRange?: MonthRangeParams | null,
    filters?: ProjectFilters,
  ) => {
    const params = new URLSearchParams();
    if ('projectId' in opts) params.set('projectId', opts.projectId);
    else {
      params.set('variant', opts.variant);
      if (opts.listingVariant) params.set('listingVariant', opts.listingVariant);
    }
    if (limit != null) params.set('limit', String(limit));
    appendMonthRangeParams(params, monthRange);
    appendProjectFilterParams(params, filters);
    return getJson<{ items: LauncherEbookItem[]; total: number }>(
      `${API_BASE}/launcher-ebooks?${params.toString()}`,
    );
  },
  getProjectVideos: (category: VideoCategoryId, limit?: number) => {
    const params = new URLSearchParams();
    params.set('category', category);
    if (limit != null) params.set('limit', String(limit));
    return getJson<{ items: ProjectVideoItem[]; total: number }>(
      `${API_BASE}/videos?${params.toString()}`,
    );
  },
  getVideosByProject: (projectId: string) => {
    const params = new URLSearchParams();
    params.set('projectId', projectId);
    return getJson<{ items: ProjectVideoItem[]; total: number }>(
      `${API_BASE}/videos/by-project?${params.toString()}`,
    );
  },
  getProjectMedia: (projectId: string) =>
    getJson<{ items: ProjectMediaItem[]; total: number }>(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/media`,
    ),
  hideEdms: (edmIds: string[]) =>
    signedFetch(`${API_BASE}/edms/hide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ edmIds }),
    }).then((res) => {
      if (!res.ok) throw new Error('Failed to hide EDMs');
      return res.json();
    }),
  unhideEdms: (edmIds: string[]) =>
    signedFetch(`${API_BASE}/edms/unhide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ edmIds }),
    }).then((res) => {
      if (!res.ok) throw new Error('Failed to unhide EDMs');
      return res.json();
    }),
  tagEdmsAsEc: (edmIds: string[]) =>
    signedFetch(`${API_BASE}/edms/tag-ec`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ edmIds }),
    }).then((res) => {
      if (!res.ok) throw new Error('Failed to tag EDMs as EC');
      return res.json();
    }),
  untagEdmsAsEc: (edmIds: string[]) =>
    signedFetch(`${API_BASE}/edms/untag-ec`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ edmIds }),
    }).then((res) => {
      if (!res.ok) throw new Error('Failed to untag EDMs as EC');
      return res.json();
    }),
  getProjectActivities: () =>
    getJson<{ items: ProjectActivityItem[] }>(`${API_BASE}/project-activities`),
  getProjectActivitiesByProject: (projectId: string) => {
    const params = new URLSearchParams();
    params.set('projectId', projectId);
    return getJson<{ items: ProjectActivityItem[]; total: number }>(
      `${API_BASE}/project-activities/by-project?${params.toString()}`,
    );
  },
  getProjectMembers: (projectId: string) =>
    getJson<ProjectMembersResponse>(`${API_BASE}/projects/${encodeURIComponent(projectId)}/members`),
  getProjectAnnouncements: (projectId: string) =>
    getJson<{ items: ProjectAnnouncementItem[]; total: number }>(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/announcements`,
    ),
  getAnnouncements: (projectId?: string) => {
    const params = new URLSearchParams();
    if (projectId) params.set('projectId', projectId);
    const qs = params.toString();
    return getJson<{ items: ProjectAnnouncementItem[]; total: number }>(
      qs ? `${API_BASE}/announcements?${qs}` : `${API_BASE}/announcements`,
    );
  },
  getAnnouncementProjects: () =>
    getJson<{ items: AnnouncementProjectOption[]; total: number }>(
      `${API_BASE}/announcement-projects`,
    ),
  /**
   * Read-only list of Agent Access Folder links for the project. Write
   * operations live under the main /api namespace (super-picker admin
   * surface) — this dialog only surfaces them for browsing.
   */
  getProjectResourceLinks: (projectId: string) =>
    getJson<{ items: ProjectResourceLink[] }>(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/resource-links`,
    ),
  getGlsLandSales: (query?: string, filters?: ProjectFilters) => {
    const params = new URLSearchParams();
    const trimmed = (query || '').trim();
    if (trimmed) params.set('q', trimmed);
    appendProjectFilterParams(params, filters);
    const qs = params.toString();
    const url = qs ? `${API_BASE}/gls-land-sales?${qs}` : `${API_BASE}/gls-land-sales`;
    return getJson<{ items: GlsLandSaleItem[]; total: number }>(url);
  },
  getGlsLandSaleDetail: (id: number | string) =>
    getJson<GlsLandSaleItem>(`${API_BASE}/gls-land-sales/${encodeURIComponent(String(id))}`),
  getLandSaleByProjectId: async (projectId: string): Promise<GlsLandSaleItem | null> => {
    const response = await signedFetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/land-sale`,
    );
    if (response.status === 404) return null;
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error((payload as { error?: string })?.error || `Request failed: ${response.status}`);
    }
    return payload as GlsLandSaleItem;
  },
};

/**
 * Curated external link on a project's Agent Access Folder. Mirrors the
 * super-picker `ProjectResourceLink` shape.
 */
export interface ProjectResourceLink {
  id: number;
  projectId: string;
  url: string;
  label: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ProjectAnnouncementItem {
  id: string;
  title: string;
  content: string;
  summary: string;
  subCategory: string | null;
  projectId?: string | null;
  projectName?: string | null;
  createdAt: string | null;
}

export interface AnnouncementProjectOption {
  id: string;
  name: string;
}

export interface ProjectMemberDetail {
  userId: string;
  name: string | null;
  ceaNumber: string | null;
  mobile: string | null;
  photo: string | null;
  /** Taggers whose ManagerId points at this Lead IC / IC. */
  taggers?: ProjectMemberDetail[];
  /** PCs whose ManagerId points at this Lead IC / IC. */
  pcs?: ProjectMemberDetail[];
}

export interface ProjectMemberGroup {
  role: string;
  members: ProjectMemberDetail[];
}

export interface ProjectMembersResponse {
  members: ProjectMemberGroup[];
}

export interface ProjectActivityItem {
  id: string;
  name: string;
  start: string | null;
  end: string | null;
  description: string | null;
  location: string | null;
  category: string | null;
  status: string | null;
  colorCode: string | null;
  thumbnail: string | null;
}
